import { type WorkspacePostingStore } from '@/features/admin/staffing/WorkspacePostingStore'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { type WorkspacePosting } from '@/features/admin/staffing/WorkspacePosting'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { ApplicantManagementPanelViewModel } from '@/features/admin/staffing/components/ApplicantManagementPanel/ApplicantManagementPanelViewModel'
import {
  type WorkspaceApplicantStatusVariant,
  type WorkspacePostingStatusVariant,
  formatWorkspaceApplicantStatus,
  formatWorkspacePostingStatus,
  workspaceApplicantStatusVariant,
  workspacePostingStatusVariant,
} from '@/features/admin/staffing/workspace-posting-formatters'
import { capitalizeFirstLetter } from '@/utils/string-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * An applicant of the shown posting, ready for display.
 */
export interface StaffingDetailsApplicant {
  userPublicId: Id
  avatar?: string | null
  displayName: string
  displayStatus: string
  statusVariant: WorkspaceApplicantStatusVariant
}

/**
 * The shown posting, ready for display.
 */
export interface StaffingDetailsPosting {
  workspaceName: string
  location: string
  primarySoftware: string
  backupFileNeeded: boolean
  supplementalSoftware: string
  capacity: number
  specialtiesText: string
  typeOfWorkText: string
  description: string
  displayStatus: string
  statusVariant: WorkspacePostingStatusVariant
  applicants: StaffingDetailsApplicant[]
}

export class StaffingDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel({
    onClose: () => this._clearWorkspacePosting(),
  })

  @observable accessor workspacePosting: WorkspacePosting | null = null
  @observable accessor applicantsExpanded = true

  /**
   * View model for the applicant management panel.
   */
  readonly applicantManagementPanelVm

  constructor(
    private readonly _workspacePostingStore: WorkspacePostingStore,
    private readonly _openUpdateWorkspacePostingDialog: (
      workspacePosting: WorkspacePosting,
    ) => void,
    private readonly _closeNav: () => void,
    private readonly _newAlert: AlertFactory,
    private readonly _refreshStatusFilter: () => Promise<void>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.applicantManagementPanelVm = new ApplicantManagementPanelViewModel(
      this._workspacePostingStore,
      () => this.workspacePosting,
      this._createAlertErrorHandler,
      this._createAlertRetryWrapper,
    )

    this._lifecycle.manage(this.applicantManagementPanelVm)
  }

  /**
   * The selected posting, ready for display.
   */
  @computed
  get postingDetails(): StaffingDetailsPosting | null {
    const posting = this.workspacePosting
    if (!posting) {
      return null
    }

    return {
      workspaceName: posting.workspace.name,
      location: posting.location,
      primarySoftware: posting.primarySoftware,
      backupFileNeeded: posting.backupFileNeeded,
      supplementalSoftware: posting.supplementalSoftware,
      capacity: posting.capacity,
      specialtiesText: posting.specialties.join(', '),
      typeOfWorkText: posting.jobTypes
        .map((j) => capitalizeFirstLetter(j))
        .join(', '),
      description: posting.description,
      displayStatus: formatWorkspacePostingStatus(posting.status),
      statusVariant: workspacePostingStatusVariant(posting.status),
      applicants: posting.applicants.map((applicant) => ({
        userPublicId: applicant.userPublicId,
        avatar: applicant.avatar,
        displayName: applicant.displayName,
        displayStatus: formatWorkspaceApplicantStatus(applicant.status),
        statusVariant: workspaceApplicantStatusVariant(applicant.status),
      })),
    }
  }

  @computed
  get showActions() {
    return this.isNotComplete
  }

  @computed
  get showManageButton() {
    return this.isNotComplete && !this.isInDraft
  }

  @computed
  get isNotComplete() {
    return (
      this.workspacePosting?.status !== 'Closed' &&
      this.workspacePosting?.status !== 'Cancelled'
    )
  }

  @computed
  get isInDraft() {
    return this.workspacePosting?.status === 'Draft'
  }

  @computed
  get showMarkInReview() {
    return this.workspacePosting?.status === 'Active'
  }

  @computed
  get showInReviewActions() {
    return this.workspacePosting?.status === 'InReview'
  }

  /**
   * Loads the posting, then opens it in the panel.
   * @param workspacePosting
   */
  @action.bound
  async show(workspacePosting: WorkspacePosting) {
    try {
      await this._fetchWorkspacePosting(workspacePosting.id)
    } catch {
      // The panel only opens on a loaded posting.
      return
    }

    runInAction(() => {
      this.workspacePosting = workspacePosting
      this.applicantsExpanded = true
      this.modal.show()
    })
    this._closeNav()
  }

  @action.bound
  toggleApplicantsExpanded() {
    this.applicantsExpanded = !this.applicantsExpanded
  }

  /**
   * Opens update workspace posting dialog with current workspace posting.
   */
  @action.bound
  updateDraft() {
    if (!this.workspacePosting) {
      return
    }

    this._openUpdateWorkspacePostingDialog(this.workspacePosting)
  }

  /**
   * Reactivates an InReview Workspace Posting
   */
  reactivate = task.resolved(async () => {
    await this._updatePosting(
      (id) => this._workspacePostingStore.reactivatePosting(id),
      'Workspace posting is now active.',
      'Reactivating the posting failed',
    )
  })

  /**
   * Manually close an InReview Workspace Posting
   */
  manuallyClose = task.resolved(async () => {
    await this._updatePosting(
      (id) => this._workspacePostingStore.manuallyClose(id),
      'Workspace posting is now closed.',
      'Closing the posting failed',
    )
  })

  /**
   * Publishes a draft Workspace Posting
   */
  publish = task.resolved(async () => {
    await this._updatePosting(
      (id) => this._workspacePostingStore.publishWorkspacePosting(id),
      'Workspace posting published.',
      'Publishing the posting failed',
    )
  })

  /**
   * Marks an active Workspace Posting in review
   */
  markInReview = task.resolved(async () => {
    await this._updatePosting(
      (id) => this._workspacePostingStore.markInReview(id),
      'Workspace posting marked in review.',
      'Marking the posting in review failed',
    )
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _updatePosting(
    update: (id: Id) => Promise<unknown>,
    successMessage: string,
    failureTitle: string,
  ) {
    if (!this.workspacePosting) {
      return
    }

    const postingId = this.workspacePosting.id
    await update(postingId).catch(this._createAlertErrorHandler(failureTitle))

    this._newAlert()
      .withState('success')
      .saying('Success', successMessage)
      .autoDismiss(2000)
      .show()

    await this._refreshStatusFilter()
    // The update succeeded; a dismissed refresh leaves the panel as it was.
    await this._fetchWorkspacePosting(postingId).catch(() => undefined)
  }

  private async _fetchWorkspacePosting(id: Id) {
    const withRetry = this._createAlertRetryWrapper(
      'Loading the posting failed',
    )
    await withRetry(() => this._workspacePostingStore.fetchWorkspacePosting(id))
  }

  @action.bound
  private _clearWorkspacePosting() {
    this.workspacePosting = null
  }
}
